/*
 * ArgoCD Application Parameters Hook
 * 获取 ArgoCD 应用的实时配置参数（镜像 tag、副本数、历史版本等）
 */

import { useState, useCallback, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { argoCDApiRef } from '@roadiehq/backstage-plugin-argo-cd';

export interface RestartableResource {
  name: string;
  kind: 'Deployment' | 'StatefulSet' | 'DaemonSet';
  namespace: string;
  replicas?: number;
}

export interface ArgoCDAppParams {
  // 当前镜像信息
  currentImages: string[];
  currentImageTag?: string;

  // 副本数信息
  currentReplicas?: number;
  desiredReplicas?: number;

  // 版本历史
  historyRevisions: HistoryRevision[];
  currentRevision?: string;

  // 健康和同步状态
  healthStatus?: string;
  syncStatus?: string;

  // 可重启的资源列表
  restartableResources: RestartableResource[];

  // 原始应用数据
  rawApplication?: any;
}

export interface HistoryRevision {
  id: string;
  revision: string;
  deployedAt: string;
  source?: {
    repoURL?: string;
    targetRevision?: string;
    path?: string;
  };
}

interface UseArgoCDAppParamsOptions {
  appName: string;
  appNamespace?: string;
  instanceName: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const useArgoCDAppParams = ({
  appName,
  appNamespace = 'argocd',
  instanceName,
  autoRefresh = false,
  refreshInterval = 10000,
}: UseArgoCDAppParamsOptions): {
  params: ArgoCDAppParams | null;
  loading: boolean;
  error: Error | undefined;
  refresh: () => Promise<void>;
} => {
  const [params, setParams] = useState<ArgoCDAppParams | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  const api = useApi(argoCDApiRef);

  const fetchAppParams = useCallback(async () => {
    if (!appName) {
      setParams(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // 获取应用详情
      const appDetails = await api.getAppDetails({
        url: `/argoInstance/${instanceName}`,
        appName,
        appNamespace,
        instance: instanceName,
      });

      if (!appDetails) {
        throw new Error(`Application ${appName} not found`);
      }

      const appDetailsAny = appDetails as any;

      // 从 ArgoCD Application 的 spec.source.plugin.env 中提取参数
      // 这是 ArgoCD PARAMETERS 标签页显示的内容
      const pluginEnv = appDetailsAny.spec?.source?.plugin?.env || [];

      // 提取 IMAGE_TAG
      const imageTagEnv = pluginEnv.find((env: any) => env.name === 'IMAGE_TAG');
      const currentImageTag = imageTagEnv?.value;

      // 提取 REPLICAS
      const replicasEnv = pluginEnv.find((env: any) => env.name === 'REPLICAS');
      let currentReplicas: number | undefined;
      if (replicasEnv?.value) {
        currentReplicas = parseInt(replicasEnv.value, 10);
        if (isNaN(currentReplicas)) {
          currentReplicas = undefined;
        }
      }

      // 如果 plugin env 中没有值，直接使用 undefined，让 Pipeline 默认值生效
      const images = appDetailsAny.status?.summary?.images || [];

      // 提取可重启的资源列表（Deployment、StatefulSet、DaemonSet）
      const restartableResources: RestartableResource[] = [];
      if (appDetailsAny.status?.resources) {
        appDetailsAny.status.resources.forEach((resource: any) => {
          if (
            resource.kind === 'Deployment' ||
            resource.kind === 'StatefulSet' ||
            resource.kind === 'DaemonSet'
          ) {
            // 尝试从多个位置获取副本数
            const resourceReplicas = resource.spec?.replicas
              || resource.status?.replicas
              || resource.status?.readyReplicas;

            restartableResources.push({
              name: resource.name,
              kind: resource.kind,
              namespace: resource.namespace || appNamespace || 'default',
              replicas: resourceReplicas,
            });

            // 如果还没有获取到 currentReplicas，从资源中获取
            if (currentReplicas === undefined && resourceReplicas !== undefined) {
              currentReplicas = resourceReplicas;
            }
          }
        });
      }

      // 提取历史版本信息
      const history = appDetailsAny.status?.history || [];
      const historyRevisions: HistoryRevision[] = history.map((h: any) => ({
        id: h.id?.toString() || h.revision,
        revision: h.revision || 'unknown',
        deployedAt: h.deployedAt || new Date().toISOString(),
        source: {
          repoURL: h.source?.repoURL,
          targetRevision: h.source?.targetRevision,
          path: h.source?.path,
        },
      }));

      // 当前 revision
      const currentRevision = appDetailsAny.status?.sync?.revision;

      // 健康和同步状态
      const healthStatus = appDetails.status?.health?.status;
      const syncStatus = appDetails.status?.sync?.status;

      setParams({
        currentImages: images,
        currentImageTag,
        currentReplicas,
        desiredReplicas: currentReplicas,
        historyRevisions,
        currentRevision,
        healthStatus,
        syncStatus,
        restartableResources,
        rawApplication: appDetails,
      });

      setError(undefined);
    } catch (err) {
      setError(err as Error);
      setParams(null);
    } finally {
      setLoading(false);
    }
  }, [api, appName, appNamespace, instanceName]);

  useEffect(() => {
    fetchAppParams();
  }, [fetchAppParams]);

  // 自动刷新
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchAppParams();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchAppParams]);

  return {
    params,
    loading,
    error,
    refresh: fetchAppParams,
  };
};

